import { Button, Drawer } from "@etudescarre/ui";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { toaster } from "@/app";
import { apiClient } from "@/lib/api/client";

type StatusFormData = {
  valeurCompany: string;
  mappingChantierCarre?: string;
};

type Status = {
  id: number;
  valeurCompany: string;
  mappingChantierCarre: string | null;
};

export type StatusFormProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  subscriberId: number;
  companyId: number;
  status?: Status;
  onSuccess?: () => void;
};

export function StatusForm({
  open,
  onOpenChange,
  subscriberId,
  companyId,
  status,
  onSuccess,
}: StatusFormProps) {
  const isEdit = !!status;

  const {
    register,
    handleSubmit,
    formState: { isSubmitting },
    reset,
  } = useForm<StatusFormData>({
    defaultValues: {
      valeurCompany: status?.valeurCompany || "",
      mappingChantierCarre: status?.mappingChantierCarre || "",
    },
  });

  useEffect(() => {
    if (open) {
      reset({
        valeurCompany: status?.valeurCompany || "",
        mappingChantierCarre: status?.mappingChantierCarre || "",
      });
    }
  }, [open, status, reset]);

  const onSubmit = async (data: StatusFormData) => {
    try {
      if (isEdit) {
        const { error } = await apiClient.PATCH(
          "/api/admin/companies/statuses/{id}",
          {
            params: { path: { id: status.id } },
            body: data,
          },
        );

        if (error) {
          toaster.error({
            title: "Erreur",
            description: error.error || "Échec de la modification du statut",
          });
          return;
        }

        toaster.success({
          title: "Succès",
          description: "Statut modifié avec succès",
        });
      } else {
        const { error } = await apiClient.POST(
          "/api/admin/companies/statuses",
          {
            body: { ...data, subscriberId, companyId },
          },
        );

        if (error) {
          toaster.error({
            title: "Erreur",
            description: error.error || "Échec de la création du statut",
          });
          return;
        }

        toaster.success({
          title: "Succès",
          description: "Statut créé avec succès",
        });
      }

      onSuccess?.();
      onOpenChange(false);
    } catch {
      toaster.error({
        title: "Erreur",
        description: "Une erreur est survenue",
      });
    }
  };

  return (
    <Drawer.Root
      open={open}
      onOpenChange={(details) => onOpenChange(details.open)}
    >
      <Drawer.Portal>
        <Drawer.Backdrop />
        <Drawer.Positioner>
          <Drawer.Content>
            <div className="flex items-center justify-between border-b border-zinc-200 p-6 dark:border-zinc-800">
              <div>
                <Drawer.Title>
                  {isEdit ? "Modifier le statut" : "Créer un statut"}
                </Drawer.Title>
                <Drawer.Description>
                  {isEdit
                    ? "Modifiez les informations du statut"
                    : "Créez un nouveau statut"}
                </Drawer.Description>
              </div>
              <Drawer.CloseTrigger />
            </div>

            <form
              onSubmit={handleSubmit(onSubmit)}
              className="flex min-h-0 flex-1 flex-col"
            >
              <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-6">
                <div>
                  <label
                    htmlFor="valeurCompany"
                    className="mb-1 block text-sm font-medium text-zinc-900 dark:text-zinc-100"
                  >
                    Valeur entreprise <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="valeurCompany"
                    type="text"
                    {...register("valeurCompany")}
                    required
                    className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100"
                    placeholder="Valeur du statut pour l'entreprise"
                  />
                </div>

                <div>
                  <label
                    htmlFor="mappingChantierCarre"
                    className="mb-1 block text-sm font-medium text-zinc-900 dark:text-zinc-100"
                  >
                    Mapping ChantierCarré
                  </label>
                  <input
                    id="mappingChantierCarre"
                    type="text"
                    {...register("mappingChantierCarre")}
                    className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100"
                    placeholder="Valeur standard ChantierCarré"
                  />
                </div>
              </div>

              <div className="flex shrink-0 justify-end gap-3 border-t border-zinc-200 p-6 dark:border-zinc-800">
                <Button
                  type="button"
                  outline
                  onClick={() => onOpenChange(false)}
                  disabled={isSubmitting}
                >
                  Annuler
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "En cours..." : isEdit ? "Modifier" : "Créer"}
                </Button>
              </div>
            </form>
          </Drawer.Content>
        </Drawer.Positioner>
      </Drawer.Portal>
    </Drawer.Root>
  );
}
